import { FormProvider } from 'react-hook-form';
import { useTheme } from '../../hooks/useTheme';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Card, ProgressBar } from '@dsi/react-eds';
import { useJFrogSettingsForm } from './jfrog/use-jfrog-settings-form';
import JFrogSettingsForm from './jfrog/JFrogSettingsForm';
import ImagePathTagUI from './jfrog/ImagePathTagUI';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { useEffect, useState } from 'react';
import api from '../../api/axiosInstance';
import { AxiosError } from 'axios';
import { useLoader } from '../../hooks/useLoader';
import styles from './ExternalRepositories.module.css';
import { RepositoryType, repositoryTypes, getRepositoryTypeInfo } from '../../utils/repositoryTypes';
import DeleteRepositoryDialog from './DeleteRepositoryDialog';
import { LOADER_DEBOUNCE_MS } from '../../const/global-constants';

/**
 * External Repositories page
 * Configure external software repositories (JFrog, GitHub, Azure, Google)
 * Uses card-based layout to support multiple repository types
 */
function ExternalRepositories() {
  const { theme } = useTheme();
  const jfrogForm = useJFrogSettingsForm({});
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();
  const [selectedType, setSelectedType] = useState<RepositoryType | null>(null);
  const [jfrogRepositories, setJfrogRepositories] = useState<any[]>([]);
  const [editingRepoId, setEditingRepoId] = useState<string | null>(null);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [testingStep, setTestingStep] = useState<string>('');
  const [testProgress, setTestProgress] = useState<number>(0);
  const [deleteRepo, setDeleteRepo] = useState<{ id: string; name: string } | null>(null);

  const fetchJfrogRepositories = async () => {
    try {
      showLoader();

      // Fetch JFrog repositories from backend
      const resp = await api.get('/api/software-repositories?type=jfrog');
      setJfrogRepositories(resp.data || []);
    } catch (error: unknown) {
      setJfrogRepositories([]);
      addNotifications([
        {
          title: 'Error - JFrog Repositories',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching JFrog repositories.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  };

  // Fetch repositories on mount
  useEffect(() => {
    fetchJfrogRepositories();
  }, []);

  const handleJFrogSettingsSubmit = async () => {
    try {
      const body = {
        name: jfrogForm.getValues('repository_name'),
        type: 'jfrog' as const,
        url: jfrogForm.getValues('repository_url'),
        username: jfrogForm.getValues('username'),
        apiToken: jfrogForm.getValues('api_token'),
        repositoryKey: jfrogForm.getValues('repository_key'),
        tlsCert: jfrogForm.getValues('tls_cert'),
        enabled: true
      };
      showLoader();

      let resp;

      if (editingRepoId) {
        // Update existing repository
        resp = await api.put(`/api/software-repositories/${editingRepoId}`, body);
      } else {
        // Create new repository
        resp = await api.post('/api/software-repositories', body);
      }

      addNotifications([
        {
          title: 'Success - JFrog Configuration',
          icon: 'icon-success',
          description: editingRepoId ? 'JFrog Artifactory updated successfully!' : 'JFrog Artifactory added successfully!',
          timestamp: new Date(),
          color: 'green'
        }
      ]);

      // Refresh the list to update status, but keep form open
      await fetchJfrogRepositories();

      // If adding new, update to editing mode with the new repo ID
      if (!editingRepoId && resp?.data?.id) {
        setEditingRepoId(resp.data.id);
      }

      return resp;
    } catch (error) {
      addNotifications([
        {
          title: 'Error - JFrog Configuration',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error?.message
              : 'An error occurred while saving the JFrog configuration',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return Promise.reject(error);
    } finally {
      setTimeout(() => hideLoader(), LOADER_DEBOUNCE_MS);
    }
  };

  const handleEditJfrogRepo = (repo: any) => {
    setEditingRepoId(repo.id);
    jfrogForm.reset({
      repository_name: repo.name,
      repository_url: repo.url,
      username: repo.username,
      api_token: repo.apiToken,
      repository_key: repo.repositoryKey,
      tls_cert: repo.tlsCert || '',
      repository_type: 'jfrog'
    });
    setSelectedType('jfrog');
  };

  const handleDeleteJfrogRepo = async (repoId: string) => {
    try {
      showLoader();

      await api.delete(`/api/software-repositories/${repoId}`);
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: 'JFrog Artifactory deleted successfully!',
          timestamp: new Date(),
          color: 'green'
        }
      ]);

      await fetchJfrogRepositories();
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error?.message
              : 'An error occurred while deleting the JFrog repository',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setTimeout(() => hideLoader(), LOADER_DEBOUNCE_MS);
    }
  };

  const handleAddNew = () => {
    setEditingRepoId(null);
    jfrogForm.reset({
      repository_name: '',
      repository_url: '',
      username: '',
      api_token: '',
      repository_key: '',
      tlsCert: ''
    });
    setSelectedType('jfrog');
  };

  return (
    <>
      <div className={theme ? 'light-theme' : 'dark-theme'}>
        {!selectedType ? (
          <>
            {/* Repository Type Selection Cards */}
            <div
              className={`${styles.settingsContainer} ${styles.settingsContainerPadding} ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
            >
              <div className={styles.settingsTitleContainer}>
                <div className={styles.settingsTitle}>Add External Repository</div>
                <p className={styles.subtitle}>Connect to external software repositories to access container images and packages from multiple vendors.</p>
              <div className={`row ${styles.repoCardRow}`}>
                {repositoryTypes.map((repoType) => (
                  <div key={repoType.type} className={`column sm-12 md-6 xl-3 ${styles.repoCardCol}`}>
                    <ReactEDSWrapper
                      component={Card}
                      title={repoType.name}
                      disabled={repoType.comingSoon}
                      className={styles.repoCard}
                    >
                      <div slot="subtitle" className={styles.repoCardSubtitle}>
                        {repoType.description}
                      </div>
                      <div slot="actions" className={styles.repoCardActions}>
                        {repoType.comingSoon ? (
                          <kbd className="tag yellow big">Coming Soon</kbd>
                        ) : (
                          <ReactEDSWrapper
                            component={Button}
                            variant="primary"
                            onClick={() => (repoType.type === 'jfrog' ? handleAddNew() : setSelectedType(repoType.type))}
                          >
                            <i className={`icon icon-plus ${styles.iconSpaced}`} />
                            Add
                          </ReactEDSWrapper>
                        )}
                      </div>
                    </ReactEDSWrapper>
                  </div>
                ))}
              </div>
              </div>
            </div>
            {/* JFrog Repositories Display */}
            {jfrogRepositories.length > 0 && (
              <div
                className={`${styles.settingsContainer} ${styles.settingsContainerPadding} ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
              >
                <div className={styles.settingsTitleContainer}>
                  <div className={styles.settingsTitle}>External Repositories</div>
                  <p className={styles.subtitle}> List of the repositories in the system
                  </p>
                  <div className="row">
                    {jfrogRepositories.map((repo) => {
                      const repoTypeInfo = getRepositoryTypeInfo('jfrog');
                      return (
                        <div key={repo.id} className="column sm-12 md-6 xl-4">
                          <ReactEDSWrapper component={Card} title={repo.name} subtitle={repoTypeInfo?.name}>
                            <div slot="subtitle">
                              <div className={styles.repoInfoRow}>
                                <i className={`icon icon-link ${styles.repoInfoIcon}`} />{repo.url}
                              </div>
                              {repo.repositoryKey && (
                                <div className={styles.repoInfoRow}>
                                  <i className={`icon icon-request-access ${styles.repoInfoIcon}`} />{repo.repositoryKey}
                                </div>
                              )}
                              <div className={styles.repoInfoRow}>
                                <kbd className={`tag ${repo.status === 'connected' ? 'green' : 'red'}`}>
                                  {repo.status === 'connected' ? 'Connected' : 'Disconnected'}
                                </kbd>
                              </div>
                              {repo.lastSync && (
                                <div className={styles.lastSync}>
                                  Last sync: {new Date(repo.lastSync).toLocaleString()}
                                </div>
                              )}
                            </div>
                            <div slot="actions" className={styles.cardActions}>
                              <ReactEDSWrapper component={Button} variant="secondary" onClick={() => handleEditJfrogRepo(repo)}>
                                <i className="icon icon-edit" />
                              </ReactEDSWrapper>
                              <ReactEDSWrapper
                                component={Button}
                                variant="secondary"
                                onClick={() => setDeleteRepo({ id: repo.id, name: repo.name })}
                              >
                                <i className="icon icon-trashcan" />
                              </ReactEDSWrapper>
                            </div>
                          </ReactEDSWrapper>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Configuration Form for Selected Repository Type */
          <>
            <div className={styles.sectionSpacing}>
              <ReactEDSWrapper component={Button} variant="secondary" onClick={() => setSelectedType(null)}>
                <i className={`icon icon-arrow-left ${styles.iconSpaced}`} />
                Back to Repository Types
              </ReactEDSWrapper>
            </div>

            <FormProvider {...jfrogForm}>
              <form onSubmit={jfrogForm.handleSubmit(handleJFrogSettingsSubmit)} className={styles.scrollableForm}>
                <div
                  className={`${styles.settingsContainer} ${styles.settingsContainerPadding} ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
                >
                  <div className={styles.settingsTitleContainer}>
                    <div className={styles.settingsTitle}>
                      {editingRepoId ? 'Edit' : 'Add'} {repositoryTypes.find((rt) => rt.type === selectedType)?.name}
                    </div>
                    <p className={styles.subtitle}>{repositoryTypes.find((rt) => rt.type === selectedType)?.description}</p>
                  </div>

                  <div className={styles.settingsContainerSize}>
                    <JFrogSettingsForm />
                  </div>

                  <div className={styles.buttonContainer}>
                    <ReactEDSWrapper
                      component={Button}
                      className="btn secondary"
                      variant="secondary"
                      type="button"
                      onClick={() => {
                        setSelectedType(null);
                        setEditingRepoId(null);
                        jfrogForm.reset();
                      }}
                    >
                      Cancel
                    </ReactEDSWrapper>
                    <ReactEDSWrapper component={Button} className="btn primary" variant="primary" type="submit">
                      Save
                    </ReactEDSWrapper>
                  </div>
                </div>
              </form>
            </FormProvider>

            <div
              className={`${styles.settingsContainer} ${styles.settingsContainerPadding} ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
            >
              <div className={styles.settingsTitleContainer}>
                <div className={styles.settingsTitle}>Connection Status</div>
                <p className={styles.subtitle}>
                  Test your JFrog Artifactory connection to verify credentials and network access.
                </p>
              </div>
              <div className={styles.connectionStatusSection}>
                <ReactEDSWrapper
                  component={Button}
                  variant="secondary"
                  disabled={isTestingConnection}
                  onClick={async () => {
                    setIsTestingConnection(true);
                    setTestProgress(0);
                    setTestingStep('Initializing connection test...');

                    try {
                      // Get form values
                      const formValues = jfrogForm.getValues();

                      // Step 1: Checking connectivity
                      setTestingStep('Step 1/3: Checking server connectivity...');
                      setTestProgress(10);
                      await new Promise((resolve) => setTimeout(resolve, 300));
                      setTestProgress(25);

                      // Step 2: Authenticating
                      setTestingStep('Step 2/3: Verifying credentials...');
                      setTestProgress(40);
                      await new Promise((resolve) => setTimeout(resolve, 300));
                      setTestProgress(55);

                      // Step 3: Testing repository access
                      setTestingStep('Step 3/3: Testing repository access...');
                      setTestProgress(70);
                      const resp = await api.post('/api/jfrog-config/test-connection', {
                        url: formValues.repository_url,
                        username: formValues.username,
                        apiToken: formValues.api_token,
                        repositoryKey: formValues.repository_key
                      });
                      setTestProgress(90);

                      // Check response status
                      if (resp.data.status === 'success') {
                        setTestProgress(100);
                        setTestingStep('Connection successful!');
                        await new Promise((resolve) => setTimeout(resolve, 500));

                        addNotifications([
                          {
                            title: 'Connection Test Successful',
                            icon: 'icon-success',
                            description: resp.data.message || 'Successfully connected to JFrog Artifactory!',
                            timestamp: new Date(),
                            color: 'green'
                          }
                        ]);

                        // If editing existing repo, update its status to connected
                        if (editingRepoId) {
                          try {
                            await api.patch(`/api/software-repositories/${editingRepoId}`, {
                              status: 'connected',
                              lastSync: new Date().toISOString()
                            });
                          } catch (error) {
                            console.error('Failed to update repository status:', error);
                          }
                        }

                        // Refresh repositories to update status in UI
                        await fetchJfrogRepositories();
                      } else {
                        setTestProgress(0);
                        addNotifications([
                          {
                            title: 'Connection Test Failed',
                            icon: 'icon-triangle-warning',
                            description: resp.data.message || 'Failed to connect to JFrog Artifactory.',
                            timestamp: new Date(),
                            color: 'red'
                          }
                        ]);
                      }
                    } catch (error) {
                      setTestProgress(0);
                      addNotifications([
                        {
                          title: 'Connection Test Failed',
                          icon: 'icon-triangle-warning',
                          description:
                            error && error instanceof AxiosError
                              ? error.response?.data?.message || error?.message
                              : 'Failed to connect to JFrog Artifactory. Please check your credentials and network.',
                          timestamp: new Date(),
                          color: 'red'
                        }
                      ]);
                    } finally {
                      setIsTestingConnection(false);
                      setTestingStep('');
                      setTestProgress(0);
                    }
                  }}
                >
                  {isTestingConnection ? (
                    <>Testing Connection...</>
                  ) : (
                    <>
                      <i className={`icon icon-play ${styles.iconSpaced}`} />
                      Test Connection
                    </>
                  )}
                </ReactEDSWrapper>

                {isTestingConnection && (
                  <div className={styles.progressBarWrapper}>
                    <ReactEDSWrapper component={ProgressBar} value={testProgress} label={testingStep} />
                  </div>
                )}
              </div>
            </div>

            {editingRepoId && (
              <div
                className={`${styles.settingsContainer} ${styles.settingsContainerPadding} ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
              >
                <ImagePathTagUI repositoryId={editingRepoId} />
              </div>
            )}
          </>
        )}
      </div>
      <DeleteRepositoryDialog
        open={!!deleteRepo}
        repositoryName={deleteRepo?.name || ''}
        onConfirm={() => {
          if (deleteRepo) handleDeleteJfrogRepo(deleteRepo.id);
          setDeleteRepo(null);
        }}
        onCancel={() => setDeleteRepo(null)}
      />
    </>
  );
}

export default ExternalRepositories;
